import * as React from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { z } from 'zod';
import classNames from 'classnames';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';

import {
  AdminUserRoleEnum,
  SalesRepUserRoleEnum,
  TempControlCalculationMethod,
  TempControlProjectStatus,
  TempControlValveFamily,
} from '__generated-api__';
import api from 'api';
import { useAuthenticatedUser } from 'auth';
import { setLocationSearchParams } from 'utils/router';
import useDocumentTitle from 'hooks/useDocumentTitle';
import Icon from 'components/icon';
import Form from 'components/Form/Form';
import { AutoSubmit } from 'components/Form/AutoSubmit';
import CheckboxField from 'components/Form/CheckboxField';
import SelectField from 'components/Form/SelectField';
import { MainHero } from 'my-account/components/MainHero';
import { DataTableHero } from 'my-account/components/DataTable/Hero';
import { DataTableClearFilterForm, DataTableListing } from 'my-account/components/DataTable/Listing';
import { SelectUser } from 'my-account/components/SelectUser';
import { getUserName } from 'my-account/utils/user';
import { buildWizardUrl } from 'tempcontrol/utils/wizard-url';
import { CALCULATION_METHOD_LABEL, parseCalculationMethod } from 'tempcontrol/utils/calculation-method';

const STATUS_LABEL: Record<TempControlProjectStatus, string> = {
  [TempControlProjectStatus.Draft]: 'Draft',
  [TempControlProjectStatus.Completed]: 'Completed',
};

const FAMILY_LABEL: Record<TempControlValveFamily, string> = {
  [TempControlValveFamily.Digital]: 'Digital',
  [TempControlValveFamily.Conventional]: 'Thermostatic',
};

const METHOD_LABEL = CALCULATION_METHOD_LABEL;

const statusZodEnum = z.nativeEnum(TempControlProjectStatus).or(z.literal(''));
const valveFamilyZodEnum = z.nativeEnum(TempControlValveFamily).or(z.literal(''));
const methodZodEnum = z.nativeEnum(TempControlCalculationMethod).or(z.literal(''));

const tempControlFiltersSchema = z.object({
  status: statusZodEnum.optional().default(''),
  valve_family: valveFamilyZodEnum.optional().default(''),
  method: methodZodEnum.optional().default(''),
  user: z
    .object({
      id: z.number().positive(),
      first_name: z.string().nullable(),
      last_name: z.string().nullable(),
    })
    .nullable()
    .optional(),
});

const TempControlFilters: React.VFC = () => {
  const user = useAuthenticatedUser();
  const history = useHistory();
  const location = useLocation();
  const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isPrivileged = user.role === AdminUserRoleEnum.Admin || user.role === SalesRepUserRoleEnum.SalesRep;

  const initialValueParse = tempControlFiltersSchema.safeParse({
    status: searchParams.get('status') ?? '',
    valve_family: searchParams.get('valve_family') ?? '',
    method: searchParams.get('method') ?? '',
    user: searchParams.has('user_id')
      ? { id: Number(searchParams.get('user_id')!), first_name: 'Loading...', last_name: '' }
      : undefined,
  });

  return (
    <Form
      schema={tempControlFiltersSchema}
      initialValues={initialValueParse.success ? initialValueParse.data : {}}
      onSubmit={async (values) => {
        history.replace(
          setLocationSearchParams(location, {
            status: values.status ? values.status : undefined,
            valve_family: values.valve_family ? values.valve_family : undefined,
            method: values.method ? values.method : undefined,
            user_id: values.user ? String(values.user.id) : undefined,
            page: undefined,
            'list-page': undefined,
          })
        );
      }}
      isFiltersForm
    >
      <DataTableClearFilterForm />
      <AutoSubmit />

      <SelectField
        name="status"
        label="Status"
        elStyle="fill"
        selectProps={{ autoComplete: 'off' }}
        allowEmpty
        emptyOptionLabel="Any Status"
        small
      >
        <option value={TempControlProjectStatus.Draft}>Draft</option>
        <option value={TempControlProjectStatus.Completed}>Completed</option>
      </SelectField>

      <SelectField
        name="valve_family"
        label="Valve Family"
        elStyle="fill"
        selectProps={{ autoComplete: 'off' }}
        allowEmpty
        emptyOptionLabel="Any Family"
        small
      >
        <option value={TempControlValveFamily.Digital}>Digital</option>
        <option value={TempControlValveFamily.Conventional}>Thermostatic</option>
      </SelectField>

      <SelectField
        name="method"
        label="Calculation Method"
        elStyle="fill"
        selectProps={{ autoComplete: 'off' }}
        allowEmpty
        emptyOptionLabel="Any Method"
        small
      >
        <option value={TempControlCalculationMethod.Hunters}>Hunter's</option>
        <option value={TempControlCalculationMethod.Symmons}>Symmons</option>
      </SelectField>

      {isPrivileged && (
        <SelectUser
          elStyle="fill"
          name="user"
          label="Project Owner"
          placeholder="Select User"
          selectProps={{ autoComplete: 'off', isClearable: true }}
          small
        />
      )}
    </Form>
  );
};

const ownershipActionsSchema = z.object({
  mine: z.boolean().optional(),
  anonymous: z.boolean().optional(),
});

/**
 * Admin / sales-rep-only ownership toggle row. Consciously kept as
 * two separate boolean toggles (rather than a tri-state) so the URL
 * stays self-describing and AutoSubmit-friendly.
 */
const TempControlOwnershipActions: React.VFC = () => {
  const history = useHistory();
  const location = useLocation();
  const searchParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);

  const ownership = searchParams.get('ownership');

  return (
    <Form
      schema={ownershipActionsSchema}
      initialValues={{
        mine: ownership === 'mine',
        anonymous: ownership === 'anonymous',
      }}
      onSubmit={async (values) => {
        history.replace(
          setLocationSearchParams(location, {
            ownership: values.mine ? 'mine' : values.anonymous ? 'anonymous' : undefined,
            page: undefined,
            'list-page': undefined,
          })
        );
      }}
      className="u-mb-0"
      stopSubmitPropagation
    >
      <AutoSubmit />
      <div className="u-flex u-flex-wrap" style={{ gap: '0.75rem' }}>
        <CheckboxField
          name="mine"
          label="Show only my projects"
          checkboxStyle="toggle"
          small
          className="u-my-spacer-base-small"
        />
        <CheckboxField
          name="anonymous"
          label="Show anonymous projects"
          checkboxStyle="toggle"
          small
          className="u-my-spacer-base-small"
        />
      </div>
    </Form>
  );
};

const formatCreated = (project: { created_at?: string }) => {
  if (!project.created_at) return null;
  try {
    return format(parseISO(project.created_at), 'MMM d y');
  } catch {
    return null;
  }
};

export default function TempControlProjectsPage() {
  useDocumentTitle('TempControl Sizing Projects');
  const user = useAuthenticatedUser();
  const isPrivileged = user.role === AdminUserRoleEnum.Admin || user.role === SalesRepUserRoleEnum.SalesRep;

  return (
    <>
      <MainHero />

      {/* The public wizard lives on the WP root domain, outside the
          my-account `BrowserRouter` basename; the "New project" CTA
          renders as an `<a href>` (built via `buildWizardUrl`) so it
          navigates out of the SPA cleanly. */}
      <DataTableHero
        title="TempControl Sizing Projects"
        actions={isPrivileged ? <TempControlOwnershipActions /> : null}
        buttons={
          <a href={buildWizardUrl()} className="c-button c-button--primary c-button--small u-my-spacer-base-small">
            <Icon name="add" className="o-svg-icon o-svg-right" />
            <span>New project</span>
          </a>
        }
      />

      <section className="c-block c-block--spacing-b-extra-small c-block--spacing-b@md">
        <div className="o-container-fluid">
          <DataTableListing
            label="TempControl projects"
            availableSortOptions={{
              created_at: 'Latest',
              updated_at: 'Recently Updated',
              project_name: 'Project Name',
              status: 'Status',
            }}
            defaultSort="created_at"
            defaultDirection="desc"
            queryFn={api.tempcontrol.listTempControlProjects}
            queryFnParams={(filters, searchParams) => {
              let status: TempControlProjectStatus | undefined;
              const rawStatus = searchParams.get('status');
              if (rawStatus === TempControlProjectStatus.Draft || rawStatus === TempControlProjectStatus.Completed) {
                status = rawStatus;
              }

              const rawFamily = searchParams.get('valve_family');
              const valveFamily =
                rawFamily === TempControlValveFamily.Digital || rawFamily === TempControlValveFamily.Conventional
                  ? rawFamily
                  : undefined;

              const rawMethod = searchParams.get('method');
              const method =
                rawMethod === TempControlCalculationMethod.Symmons || rawMethod === TempControlCalculationMethod.Hunters
                  ? rawMethod
                  : undefined;

              let ownership: 'mine' | 'anonymous' | undefined;
              const rawOwnership = searchParams.get('ownership');
              if (rawOwnership === 'mine' || rawOwnership === 'anonymous') {
                ownership = rawOwnership;
              }

              let userId: number | undefined = Number(searchParams.get('user_id'));
              if (Number.isNaN(userId) || userId < 1) {
                userId = undefined;
              }

              return {
                ...filters,
                status,
                valveFamily,
                method,
                ownership,
                userId,
              };
            }}
            filters={() => <TempControlFilters />}
          >
            {(data) => (
              <>
                {data.map((project) => (
                  <Link
                    key={project.key}
                    to={`/tempcontrol-projects/${project.key}`}
                    className={classNames('c-data-card', { 'c-data-card--companies': isPrivileged })}
                  >
                    <div className="c-data-card__column c-data-card__column--first">
                      {project.status && (
                        <p
                          className={classNames('c-data-card__subtitle', 'c-data-card__subtitle--status', {
                            'c-data-card__subtitle--draft': project.status === TempControlProjectStatus.Draft,
                            'c-data-card__subtitle--published': project.status === TempControlProjectStatus.Completed,
                          })}
                        >
                          {STATUS_LABEL[project.status as TempControlProjectStatus] ?? project.status}
                          {project.valve_family && (
                            <span> | {FAMILY_LABEL[project.valve_family] ?? project.valve_family}</span>
                          )}
                        </p>
                      )}
                      <p className="c-data-card__title">{project.project_name}</p>
                      <p className="c-data-card__label">Project Name</p>
                    </div>

                    {isPrivileged && (
                      <div className="c-data-card__column">
                        <p>{project.user ? getUserName(project.user).name : <em>Anonymous</em>}</p>
                        <p className="c-data-card__label">Owner</p>
                      </div>
                    )}

                    <div className="c-data-card__column">
                      <p>{formatCreated(project) ?? <em>—</em>}</p>
                      <p className="c-data-card__label">Date Created</p>
                    </div>

                    <div className="c-data-card__column">
                      <p>{project.method ? METHOD_LABEL[parseCalculationMethod(project.method)] : <em>—</em>}</p>
                      <p className="c-data-card__label">Method</p>
                    </div>

                    <div className="c-data-card__column c-data-card__column--last">
                      <button className="c-link-cta-basic c-link-cta--small" type="button">
                        <span>Project Details</span>
                        <Icon name="arrow" className="o-svg-icon o-svg-right" />
                      </button>
                    </div>
                  </Link>
                ))}
              </>
            )}
          </DataTableListing>
        </div>
      </section>
    </>
  );
}
